import { LOCALES, type Language } from './i18n';

const TIMEZONE = 'Asia/Tbilisi';

/**
 * Not every browser ships Georgian date data — several fall back to en-US
 * silently, which would print "Sep 28, 2026" to a Georgian customer. The
 * check is made once at runtime so browsers that do have it keep using Intl.
 */
const HAS_GEORGIAN_DATES = Intl.DateTimeFormat.supportedLocalesOf(['ka']).length > 0;

const KA_MONTHS_SHORT = [
  'იან', 'თებ', 'მარ', 'აპრ', 'მაი', 'ივნ',
  'ივლ', 'აგვ', 'სექ', 'ოქტ', 'ნოე', 'დეკ',
];

const KA_MONTHS_LONG = [
  'იანვარი', 'თებერვალი', 'მარტი', 'აპრილი', 'მაისი', 'ივნისი',
  'ივლისი', 'აგვისტო', 'სექტემბერი', 'ოქტომბერი', 'ნოემბერი', 'დეკემბერი',
];

const asDate = (value: string | Date): Date =>
  typeof value === 'string' ? new Date(value) : value;

/** Reads the calendar parts in Tbilisi time rather than the viewer's zone. */
function tbilisiParts(date: Date): { day: number; month: number; year: number } {
  const parts = new Intl.DateTimeFormat('en-GB', {
    day: 'numeric', month: 'numeric', year: 'numeric', timeZone: TIMEZONE,
  }).formatToParts(date);
  const find = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return { day: find('day'), month: find('month'), year: find('year') };
}

export function formatGel(tetri: number, language: Language): string {
  const negative = tetri < 0;
  const abs = Math.abs(tetri);
  const major = new Intl.NumberFormat(LOCALES[language]).format(Math.floor(abs / 100));
  return `${negative ? '−' : ''}₾${major}.${String(abs % 100).padStart(2, '0')}`;
}

export const formatGelWhole = (tetri: number, language: Language): string =>
  `₾${new Intl.NumberFormat(LOCALES[language]).format(Math.round(tetri / 100))}`;

export const formatNumber = (value: number, language: Language): string =>
  new Intl.NumberFormat(LOCALES[language]).format(value);

export const formatRate = (basisPoints: number): string => `${basisPoints / 100}%`;

export function formatDate(value: string | Date, language: Language): string {
  const date = asDate(value);
  if (language === 'ka' && !HAS_GEORGIAN_DATES) {
    const { day, month, year } = tbilisiParts(date);
    return `${day} ${KA_MONTHS_SHORT[month - 1]} ${year}`;
  }
  return new Intl.DateTimeFormat(LOCALES[language], { dateStyle: 'medium', timeZone: TIMEZONE })
    .format(date);
}

/**
 * Just the clock, for stages that all happen on the same day.
 *
 * Forced to 24 hours: Georgian locale data defaults to AM/PM, which nobody in
 * Tbilisi writes, and a shop floor reads times off a wall clock.
 */
export function formatClock(value: string | Date, language: Language): string {
  return new Intl.DateTimeFormat(LOCALES[language], {
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: TIMEZONE,
  }).format(asDate(value));
}

export function formatMonthYear(value: string | Date, language: Language): string {
  const date = asDate(value);
  if (language === 'ka' && !HAS_GEORGIAN_DATES) {
    const { month, year } = tbilisiParts(date);
    return `${KA_MONTHS_LONG[month - 1]} ${year}`;
  }
  return new Intl.DateTimeFormat(LOCALES[language], {
    month: 'long', year: 'numeric', timeZone: TIMEZONE,
  }).format(date);
}
